import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  message,
  Popconfirm,
  Space,
  Table,
  Tooltip,
} from "antd";
import { voteApi } from "api/vote.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { areaStore } from "store/areaStore";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { Vote } from "types/vote";
import { getTitle } from "utils";
import { formatDate } from "utils/date";
import { $url } from "utils/url";
import { VoteModal } from "./components/VoteModal";
// import { StaffModal } from "./components/StaffModal";

const { Column } = Table;

interface NewsQuery extends QueryParam {
  areaId: number | null;
}

export const VotePage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<NewsQuery>({
    page: 1,
    limit: 10,
    search: "",
    areaId: null,
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Vote[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedVote, setSelectedVote] = useState<Vote>();
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  useEffect(() => {
    document.title = getTitle(title);
    areaStore.fetchList();
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await voteApi.findAll(query);
    setLoading(false);
    setData(res.data.votes);
    setTotal(res.data.total);
  };

  const handleDelete = async (news: Vote) => {
    setLoading(true);
    try {
      await voteApi.delete(news.id);
      fetchData();
      message.info("Xóa thành công");
    } finally {
      setLoading(false);
    }
  };

  // const handleToggle = async (news: Vote) => {
  //   setLoading(true);
  //   try {
  //     if (news) {
  //       await newsApi.update(news.id, {
  //         news: {
  //           isVisible: false,
  //         },
  //       });
  //       message.info("Ẩn thành công!");
  //     } else {
  //       await newsApi.update(news.id, {
  //         news: {
  //           isVisible: true,
  //         },
  //       });
  //       message.info("Hiển thị thành công!");
  //     }
  //   } finally {
  //     fetchData();
  //     setLoading(false);
  //   }
  // };

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
          filter={
            <AppTableToolbar
              search={{
                value: search,
                placeholder: "Tìm kiếm",
                onChange: (value) => {
                  setSearch(value);
                  debounceSearch(value);
                },
                inputProps: {
                  allowClear: {
                    clearIcon: (
                      <CloseCircleFilled
                        onClick={() => {
                          setSearch("");
                          setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                        }}
                      />
                    ),
                  },
                },
              }}
              leftActions={
                <AppButton
                  onClick={() => {
                    setVisibleModal(true);
                    setModalStatus("create");
                    setSelectedVote(undefined);
                  }}
                  tone="primary"
                  icon={<PlusOutlined />}
                >
                  Thêm mới
                </AppButton>
              }
              filterLabel={null}
            />
          }
          table={
            <AppTable<Vote>
              loading={loading}
              pagination={false}
              rowKey="id"
              dataSource={data}
            >
              <Column width={300} title="Nội dung" dataIndex="name" key="name" />
              <Column
                width={500}
                title="Phương án"
                dataIndex="name"
                key="name"
                render={(text, record: Vote) => (
                  <ul>
                    {record.voteAnswers.map((item) => (
                      <li key={item.id}>{item.name}</li>
                    ))}
                  </ul>
                )}
              />
              <Column
                title="Ngày tạo"
                dataIndex="createdAt"
                key="phone"
                render={(text) => <span>{formatDate(text)}</span>}
              />

              <Column
                width={100}
                title="Thao tác"
                key="action"
                align="center"
                render={(text, record: Vote) => (
                  <div className="flex gap-2 justify-center items-center">
                    <Tooltip title="Cập nhật">
                      <ActionButton
                        aria-label="Cập nhật"
                        onClick={() => {
                          setSelectedVote(record);
                          setVisibleModal(true);
                          setModalStatus("update");
                        }}
                      >
                        <EditOutlined style={{ color: "#6c84e5" }} />
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Xóa">
                      <Popconfirm
                        placement="top"
                        title={"Xác nhận xóa tin tức này?"}
                        onConfirm={() => handleDelete(record)}
                        okText="Xóa"
                        cancelText="Không"
                      >
                        <ActionButton aria-label="Xóa">
                          <DeleteOutlined style={{ color: "#ef4444" }} />
                        </ActionButton>
                      </Popconfirm>
                    </Tooltip>
                  </div>
                )}
              />
            </AppTable>
          }
          footer={
            <TableLayoutFooter
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={total}
              currentPage={query.page}
              pageSize={query.limit}
              onChange={({ limit, page }) => {
                query.page = page;
                query.limit = limit;
                setQuery({ ...query });
              }}
            />
          }
        />
      {visibleModal && (
        <VoteModal
          onSubmitOk={fetchData}
          onClose={() => setVisibleModal(false)}
          visible={visibleModal}
          selectData={selectedVote}
          status={modalStatus}
        />
      )}
    </div>
  );
});
